import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { Observable } from 'rxjs';
import { FiChecklist2ReportHelperService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-helper.service';
import { IChecklist2ReportChecklistSummary } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { SharedActionsBaseService } from '@fi-shared/fi-shared-services/shared-actions-base.service';
import { CheckpointScoringType } from '@fi-shared/checklist/checklist-enum';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { isDefined } from '@fi-shared/utils';
import { AppTextService } from '@fi-shared/app-text';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { fiTextToHtml } from '@fi-shared/utils/fi-text-to-html';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';

interface IChecklistHeaderItemInfo {
    label: string;
    value: string | number;
    isClickable?: boolean;
    onClick?: () => void;
    additionalValue?: string | number;
    isAdditionalValueClickable?: boolean;
    onAdditionalValueClick?: () => void;
}

@Component({
    selector: 'fi-checklist-details-header-information',
    templateUrl: './checklist-header-information.component.html',
    styleUrls: ['./checklist-header-information.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist-details-header-information' },
    standalone: false,
})
export class FiChecklistDetailsHeaderInformationComponent {
    @Input() checklist: IChecklist2ReportChecklistSummary;
    @Input() isChecklistInfoInformationVisible$: Observable<boolean>;
    @Input() checkpointScoringType: CheckpointScoringType;
    @Input() checkpointName: string;
    @Input() isCheckpointInfo: boolean;

    constructor(
        private fiChecklist2ReportHelperService: FiChecklist2ReportHelperService,
        private sharedActionsService: SharedActionsBaseService,
        private fiChecklist2HelperService: FiChecklistHelperService,
        private fiCheckpoint2HelperService: FiCheckpoint2HelperService,
        private appTextService: AppTextService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    getHeaderDisplayData(): IChecklistHeaderItemInfo[][] {
        let result: IChecklistHeaderItemInfo[][];
        if (this.isCheckpointInfo) {
            result = [
                [this.checklistNameDisplayData, this.checkpointNameDisplayData, this.scheduleNameDisplayData],
                [this.lastEditedDisplayData, this.dueInDisplayData, this.createdByDisplayData],
            ];

            if (this.isScoringDataVisible()) result.push([this.scoringDisplayData]);
        } else {
            result = [
                [this.checklistNameDisplayData, this.scheduleNameDisplayData, this.createdByDisplayData],
                [this.lastEditedDisplayData, this.dueInDisplayData],
            ];
        }
        return result;
    }

    private openUserViewDialog(userId: number) {
        this.sharedActionsService.openUserViewPopup(userId);
    }

    private getChecklistRemainingTime(): string {
        return this.fiChecklist2ReportHelperService.getChecklistRemainingTime(this.checklist.PeriodDueDateTime);
    }

    private isScoringDataVisible(): boolean {
        return this.isCheckpointInfo && isDefined(this.checkpointScoringType);
    }

    private getScoringText(): string {
        return this.fiCheckpoint2HelperService.getScoringText(this.checkpointScoringType);
    }

    private get checklistNameDisplayData(): IChecklistHeaderItemInfo {
        return {
            label: this.appTextService.get('checklist.checklist_name_placeholder'),
            value: fiTextToHtml(this.checklist.ChecklistName),
        };
    }

    private get scheduleNameDisplayData(): IChecklistHeaderItemInfo {
        return {
            label: this.appTextService.get('checklist.schedule_name'),
            value: fiTextToHtml(this.checklist.ScheduleName),
        };
    }

    private get checkpointNameDisplayData(): IChecklistHeaderItemInfo {
        return {
            label: this.appTextService.get('checklist.checkpoint_name_placeholder'),
            value: fiTextToHtml(this.checkpointName),
        };
    }

    private get createdByDisplayData(): IChecklistHeaderItemInfo {
        return {
            label: this.appTextService.get('common.created_by'),
            value: this.checklist.CreatedByUserName,
            isClickable: true,
            onClick: () => this.openUserViewDialog(this.checklist.CreatedByUserId),
        };
    }

    private get lastEditedDisplayData(): IChecklistHeaderItemInfo {
        return {
            label: this.appTextService.get('common.last_edited'),
            value: this.fiFormatDateService.formatDate(this.checklist.ChangedOn, undefined),
            additionalValue: this.checklist.ChangedByUserName,
            isAdditionalValueClickable: true,
            onAdditionalValueClick: () => this.openUserViewDialog(this.checklist.ChangedByUserId),
        };
    }

    private get dueInDisplayData(): IChecklistHeaderItemInfo {
        return {
            label: this.appTextService.get('common.due_in'),
            value: this.getChecklistRemainingTime(),
        };
    }

    private get scoringDisplayData(): IChecklistHeaderItemInfo {
        return {
            label: this.appTextService.get('common.scoring'),
            value: this.getScoringText(),
        };
    }
}
